import {LOCALES} from "../../config";
import {IAncillaryHistoryRecord} from "./models";

/**
 * F24 (LPP-21950) — T2.59: resa degli importi del registro delta nello storico del dettaglio RA.
 *
 * Il registro porta una **terna**: lordo (`amountTaxIncluded`), netto addebitato
 * (`deltaAmountTaxIncluded`) e importo dello sconto banco (`discountAmount`), con l'invariante
 * `lordo − netto = sconto` garantita dal backend (T2.58b). Lo storico ne mostrava **uno e mezzo**: il
 * netto e la sola *percentuale* di sconto. Lordo e importo dello sconto erano esposti dall'API e mai
 * resi, quindi l'operatore vedeva che uno sconto era stato applicato ma non di quanto, e non aveva modo
 * di ricostruire il prezzo di partenza — che è il dato su cui il cliente contesta.
 *
 * La logica sta in un modulo a parte perché è l'unica parte testabile del componente: la tabella è
 * presentazione, questa è la regola di cosa mostrare e quando.
 */

/** Percentuale in centesimi di punto → testo localizzato (`1000` → `10%`). */
export const formatDiscountPercentage = (
	value: number | null | undefined,
	locale: LOCALES
): string => (value === undefined || value === null) ? '-' : `${(value / 100).toLocaleString(locale)}%`;

/**
 * Sconto banco della riga: percentuale e, quando c'è, anche **quanto** è stato scontato
 * (`10% (-1,17 €)`).
 *
 * È la stessa forma usata sul documento accessori post-firma che il cliente firma
 * (`RaAncillaryModelBuilder.formatDiscount`): deliberatamente identica, così la schermata e il
 * documento non si leggono in due modi diversi sullo stesso dato.
 *
 * Uno sconto a **zero** non è uno sconto: si mostra la sola percentuale, come faceva il documento.
 */
export const formatDeskDiscount = (
	record: Pick<IAncillaryHistoryRecord, 'discountPercentage' | 'discountAmount'>,
	locale: LOCALES,
	// Firma allineata a `formatCurrency` di `../../utils`, che e' il formattatore reale passato dal
	// componente: il locale e' opzionale (default `LOCALES.it`) e c'e' una valuta con default.
	formatAmount: (locale: LOCALES | undefined, cents: number | null, currency?: string) => string
): string => {
	const percentage = formatDiscountPercentage(record.discountPercentage, locale);
	const amount = record.discountAmount;
	if (record.discountPercentage === undefined || record.discountPercentage === null
		|| amount === undefined || amount === null || amount === 0) {
		return percentage;
	}
	return `${percentage} (-${formatAmount(locale, Math.abs(amount))})`;
};

/**
 * Lordo da mostrare **barrato** accanto al netto, cioè il prezzo prima dello sconto.
 *
 * Si restituisce solo quando c'è davvero qualcosa da barrare: se lordo e netto coincidono il prezzo
 * pieno *è* quello addebitato e un valore barrato uguale a quello accanto sarebbe rumore. È la stessa
 * convenzione con cui l'emissione mostra un accessorio scontato.
 *
 * `null` anche sui record **pregressi** in cui il lordo era il prezzo mandato dal front-end e non
 * riconcilia con la terna (`lordo − netto ≠ sconto`): barrare un numero che non è il prezzo di partenza
 * di quello sconto mostrerebbe uno sconto diverso da quello applicato. Su quei record resta la
 * percentuale, che è il dato attendibile.
 */
export const strikethroughGross = (
	record: Pick<IAncillaryHistoryRecord, 'amountTaxIncluded' | 'deltaAmountTaxIncluded' | 'discountAmount'>
): number | null => {
	const {amountTaxIncluded: gross, deltaAmountTaxIncluded: net, discountAmount: discount} = record;
	if (gross === undefined || gross === null || net === undefined || net === null) {
		return null;
	}
	const grossAbs = Math.abs(gross);
	const netAbs = Math.abs(net);
	if (grossAbs <= netAbs) {
		return null;
	}
	const discountAbs = (discount === undefined || discount === null) ? 0 : Math.abs(discount);
	return grossAbs - netAbs === discountAbs ? grossAbs : null;
};
